"use client";
import React from "react";
import { useScreen } from "@/hooks/useScreen";
import Link from "next/link";

interface FeatureItemProps {
  icon?: string;
  title: string;
  description: string | React.ReactNode;
  noMargin?: boolean;
}

interface HowToStepProps {
  step: string;
  title: string;
  description: React.ReactNode | string;
  icon?: string;
  noMargin?: boolean;
}

// Helper component for the feature items
const FeatureItem: React.FC<FeatureItemProps> = ({
  icon,
  title,
  description,
  noMargin = false,
}) => (
  <div className={`flex items-start space-x-4 pl-4 ${noMargin ? "" : "mb-10"}`}>
    {icon ? (
      <div className="flex-shrink-0 ">
        <img
          src={icon}
          alt={title}
          className=" w-14 h-14 md:w-10 md:h-10 lg:w-20 lg:h-20 mt-2  flex items-start justify-start text-gray-600"
        />
      </div>
    ) : (
      <div className="w-10 h-10"></div>
    )}
    <div className="text-left">
      <h4 className="text-[18px] md:text-[16px] mb-2 font-bold font-['Ainslie_Sans'] text-[#920000] lg:text-[28px]">
        {title}
      </h4>
      <p className=" text-[18px] md:text-[16px] font-bold font-['Ainslie_Sans'] text-[black] lg:text-[24px] ">
        {description}
      </p>
    </div>
  </div>
);

// Helper component for the feature items
const Feature2: React.FC<FeatureItemProps> = ({
  icon,
  title,
  description,
  noMargin = false,
}) => (
  <div className={`flex  space-x-4 pl-4 ${noMargin ? "" : "mb-10"}`}>
    {icon ? (
      <div className="flex-shrink-0">
        <img
          src={icon}
          alt={title}
          className=" w-14 h-14 md:w-10 md:h-10 lg:w-20 lg:h-20 mt-2  flex items-start justify-start text-gray-600"
        />
      </div>
    ) : (
      <div className="w-10 h-10"></div>
    )}
    <div className="text-left">
      <h4 className="text-[18px] md:text-[16px] mb-2 font-bold font-['Ainslie_Sans'] text-[#920000] lg:text-[28px]">
        {title}
      </h4>
      <p className=" text-[18px] md:text-[16px] font-bold font-['Ainslie_Sans'] text-[black] lg:text-[24px] ">
        {description}
      </p>
    </div>
  </div>
);

const HowToStep: React.FC<HowToStepProps> = ({
  step,
  title,
  description,
  icon,
  noMargin = false,
}) => (
  <div className={`flex items-start space-x-4 pl-4 ${noMargin ? "" : "mb-10"}`}>
    {icon && (
      <div className="flex-shrink-0">
        <img
          src={icon}
          alt={step}
          className="w-14 h-14 md:h-10 md:w-10 lg:w-20 lg:h-20  mt-2 flex items-start justify-start text-gray-600"
        />
      </div>
    )}
    <div className="text-left">
      <h4 className="text-[18px] md:text-[16px] mb-2 font-bold font-['Ainslie_Sans'] text-[#920000] lg:text-[28px]">
        {step}
      </h4>
      <h5 className="text-[18px] md:text-[16px] font-bold font-['Ainslie_Sans'] text-[black] lg:text-[24px]">
        {title}
      </h5>
      <p className="text-[18px] md:text-[16px] font-bold font-['Ainslie_Sans'] text-[black] lg:text-[24px]">
        {description}
      </p>
    </div>
  </div>
);
// Main component as a functional component with no props
const RyzeProductSection: React.FC = () => {
  const { isMobile } = useScreen();
  return (
    <div className="font-sans py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Title */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-block border-b-2 border-gray-500 pb-4">
            <h2 className="text-[#920000] font-['Ainslie_Sans'] text-[28px] font-bold  lg:text-[64px] md:mb-4 ">
              Formulated with purpose
            </h2>
            <p className="text-[black] font-['Ainslie_Sans'] font-bold text-2xl lg:text-[54px] md:mb-8 mb-4">
              Straight from our labs
            </p>
          </div>
        </div>

        {/* Real Talk Section */}
        <div className="mb-14">
          <h3 className="text-center font-['Ainslie_Sans'] text-[20px] font-bold text-black mb-8 lg:text-[50px]">
            <span className="text-[#920000]">Real talk:</span> Why Ryze actually
            works?
          </h3>
          <div className="flex flex-col md:flex-row items-center md:space-x-8 ">
            <div className="md:w-1/2 mb-8 md:mb-0 flex-shrink-0 ">
              <img
                src={
                  isMobile
                    ? "/images/science/section1/section1mobile.webp"
                    : "/images/science/section1/Real talk_ Why Ryze actually works.webp"
                }
                alt="Hand holding Ryze gum"
                className="rounded-lg w-full h-auto object-cover"
              />
            </div>
            {/* Features List */}
            <div className="md:w-1/2 ">
              <FeatureItem
                icon="/images/science/section1/Back to 100 years.webp"
                title="Backed by 100+ years of innovation"
                description={
                  <>
                    Crafted by Fertin Pharma A/S - a global leader in NRT
                    products with over a century of expertise.
                  </>
                }
              />
              <FeatureItem
                icon="/images/science/section1/soft chew.webp"
                title="Soft to chew, easy to love"
                description={
                  <>
                    Our soft-chew texture is gentle on your jaw, effective, and
                    fits effortlessly into your daily routine.
                  </>
                }
              />
              <FeatureItem
                icon="/images/science/section1/gentle on throat.webp"
                title="Gentle on your throat"
                description={
                  <>
                    Formulated to release flavours slowly, giving you a smooth,
                    long-lasting experience without irritation.
                  </>
                }
                noMargin={true}
              />
            </div>
          </div>
        </div>

        {/* Helping You Manage Cravings Section */}
        <div className="hidden text-center mb-12">
          <h3 className="text-center font-['Ainslie_Sans'] text-[24px] font-bold text-black mb-8 lg:text-[50px] lg:my-10">
            Helping you manage cravings <br className="md:hidden" />
            <span className="text-[#920000]">safely & smartly!</span>
          </h3>
          <div className="flex flex-col md:flex-row md:items-center items-start md:space-x-8">
            <div className="md:w-1/2 mb-8 md:mb-0 flex-shrink-0 ">
              <img
                src={
                  isMobile
                    ? "/images/science/section2/section2mobile.webp"
                    : "/images/science/section2/Helping-you-manage-cravings-safely-&-smartly!.webp"
                }
                alt="Hand holding Ryze gum"
                className="rounded-lg w-full h-auto object-cover"
              />
            </div>
            {/* Features List */}
            <div className="md:w-1/2 lg:mt-8">
              <Feature2
                icon="/images/science/section2/Teeth whitening.webp"
                title="Teeth whitening"
                description="Say yes to stain-free teeth"
              />
              <Feature2
                icon="/images/science/section2/30+-years.webp"
                title="Danish R&D at its best"
                description="Developed by Danish Org"
              />
              <Feature2
                icon="/images/science/section2/Effective .webp"
                title="Effective cessation"
                description="Smooth release"
              />
              <Feature2
                icon="/images/science/section2/sugar free.webp"
                title="Sugar-free"
                description="Tasty, and doesn't add to calories"
              />
            </div>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="mb-12">
          <h3 className="text-center font-['Ainslie_Sans'] text-[32px] font-bold text-black mb-8 lg:text-[50px] lg:my-10">
            How to use <span className="text-[#920000]">Ryze?</span>
          </h3>
          <div className="flex flex-col md:flex-row items-center md:space-x-8">
            <div className="md:w-1/2 mb-8 md:mb-0 flex-shrink-0 ">
              <img
                src={
                  isMobile
                    ? "/images/science/section3/section3mobile.webp"
                    : "/images/science/section3/how-to-ryze.webp"
                }
                alt="Hand holding Ryze gum"
                className="rounded-lg w-full h-auto object-cover"
              />
            </div>
            {/* Steps List */}
            <div className="md:w-1/2 lg:mt-8">
              <HowToStep
                step="CHEW"
                title="Chew the gum slowly till"
                description="you start feeling the kick."
                icon="/images/science/section3/chew.webp"
              />
              <HowToStep
                step="PARK"
                title="Pause chewing and place Ryze gum between your cheek and gums."
                description=""
                icon="/images/science/section3/park.webp"
              />
              <HowToStep
                step="CHEW"
                title="Chew again, after the taste"
                description="fades away."
                icon="/images/science/section3/chew.webp"
              />
            </div>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="text-center">
          <p className="text-[18px] md:mb-2 font-bold font-['Ainslie_Sans'] text-[black] lg:text-[36px]">
            They say it takes <span className="text-[#920000]">21 days</span> to
            break a habit,
            <br /> make sure you're regular with Ryze!
          </p>
          <div
            className="max-w-[350px] mt-8 py-4 px-6 text-white  bg-[#ff0000] lg:max-w-[450px] mx-auto
             rounded-tl-[48px] rounded-br-[48px] rounded-tr-none rounded-bl-none font-['Ainslie_Sans'] text-[24px] font-medium mb-8 lg:text-[40px]"
          >
            <Link
              href="/collection/ryze-gums"
              aria-label="Shop Ryze Gums"
              className="text-white"
            >
              Shop now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RyzeProductSection;
